/* eslint-disable */
import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ChevronLeft,
  Loader,
  Play,
  Image,
  Music,
  Sparkles,
} from "lucide-react";
import MainHeader from "../MainHeader";

/* helpers */
const toB64Obj = (f) =>
  new Promise((res, rej) => {
    const rd = new FileReader();
    rd.onload = () =>
      res({
        data: rd.result.split(",")[1],
        ext: f.name.split(".").pop().toLowerCase(),
      });
    rd.onerror = () => rej(rd.error);
    rd.readAsDataURL(f);
  });
const toDataUri = (x) => {
  const base = typeof x === "string" ? x : x.data;
  const ext = typeof x === "string" ? "png" : x.ext || "png";
  return /^data:/i.test(base)
    ? base
    : `data:image/${ext};base64,${base.replace(/\s+/g, "")}`;
};
/* 카테고리 */
const catColor = {
  생산성: "blue",
  분석: "blue",
  크리에이티브: "blue",
  유틸리티: "blue",
};

export default function AgentExec() {
  const { api } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const agentId = params.get("x_id") || "";

  const [agent, setAgent] = useState(null);
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState("");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);

  /* 스키마 로드 */
  useEffect(() => {
    if (!agentId) return;
    (async () => {
      const { data } = await api.get(
        `/multimodal-agent/schema?x_id=${agentId}`
      );
      const a = data.data || {};
      setAgent(a);
      const schemaObj =
        typeof a.agent_schema === "string"
          ? JSON.parse(a.agent_schema)
          : a.agent_schema;
      const props = schemaObj.properties || {};
      setInputs(Object.keys(props).reduce((o, k) => ({ ...o, [k]: "" }), {}));
    })();
  }, [agentId, api]);

  /* 입력 */
  const change = useCallback(
    (k, v) => setInputs((p) => ({ ...p, [k]: v })),
    []
  );
  const fileIn = useCallback(
    async (k, f) => f && change(k, await toB64Obj(f)),
    [change]
  );

  /* 실행 */
  const exec = async () => {
    if (!agent) return;
    setLoading(true);
    try {
      const { data } = await api.post("/multimodal-agent/exec", {
        agent_id: agent.agent_id,
        agent_kind: agent.agent_kind,
        input_values: inputs,
      });
      const okTxt = data.result?.trim();
      const okImg = Array.isArray(data.images) && data.images.length;
      setResult(
        okTxt ||
          (okImg ? "아래는 생성된 이미지입니다." : "출력에 실패했습니다.")
      );
      setImages(okImg ? data.images : []);
    } finally {
      setLoading(false);
    }
  };

  if (!agent) return null;
  const color = catColor[agent.agent_category] || "indigo";
  const schema =
    typeof agent.agent_schema === "string"
      ? JSON.parse(agent.agent_schema)
      : agent.agent_schema;
  const props = schema.properties || {};

  return (
    <>
      <MainHeader />
      <div className="fixed inset-0 z-50 bg-white">
        <div
          className={`bg-gradient-to-r from-${color}-500 to-${color}-600 p-6`}
        >
          <div className="max-w-7xl mx-auto flex items-start justify-between text-white">
            <div className="flex items-start gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-white/20 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h2 className="text-2xl font-bold mb-1">{agent.agent_title}</h2>
                <p className="text-white/80">{agent.agent_description}</p>
              </div>
            </div>
            <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
              {agent.agent_category || "AI"}
            </span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto p-6">
          <div className="grid md:grid-cols-2 gap-8 h-[calc(100vh-200px)]">
            {/* 입력 */}
            <div className="bg-white rounded-xl shadow-sm border p-6 overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">입력</h3>
              <div className="space-y-4">
                {Object.entries(props).map(([k, p]) => {
                  const kind = p.input_kind || "text";
                  const accept =
                    (p.accept && p.accept.map((e) => "." + e).join(",")) ||
                    (kind === "audio" ? ".mp3,.wav,.m4a,.mp4" : ".png,.jpg");
                  return (
                    <div key={k} className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {p.description || k}
                      </label>

                      {kind === "text" && (
                        <textarea
                          rows={2}
                          value={inputs[k] || ""}
                          onChange={(e) => change(k, e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
                        />
                      )}

                      {["audio", "image"].includes(kind) && (
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400">
                          <input
                            type="file"
                            accept={accept}
                            onChange={(e) => fileIn(k, e.target.files[0])}
                            className="text-sm"
                          />
                          <label
                            htmlFor={`${k}-file`}
                            className="flex flex-col items-center cursor-pointer"
                          >
                            {kind === "audio" ? (
                              <Music className="w-8 h-8 text-gray-400 mb-2" />
                            ) : (
                              <Image className="w-8 h-8 text-gray-400 mb-2" />
                            )}
                            <span className="text-sm text-gray-600">
                              파일 선택
                            </span>
                          </label>
                        </div>
                      )}
                      {kind === "text+image" && (
                        <>
                          <textarea
                            rows={2}
                            value={inputs[k] || ""}
                            onChange={(e) => change(k, e.target.value)}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm
                                 focus:ring-2 focus:ring-blue-500 mb-2"
                          />
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400">
                            <input
                              id={`${k}-img`}
                              type="file"
                              accept=".png,.jpg"
                              onChange={
                                (e) =>
                                  fileIn(
                                    `${k}__file`,
                                    e.target.files[0]
                                  ) /* 텍스트와 구분 */
                              }
                              className="text-sm"
                            />
                            <label
                              htmlFor={`${k}-img`}
                              className="flex flex-col items-center cursor-pointer"
                            >
                              <Image className="w-8 h-8 text-gray-400 mb-2" />
                              <span className="text-sm text-gray-600">
                                이미지 선택
                              </span>
                            </label>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              <button
                onClick={exec}
                disabled={loading}
                className={`mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-${color}-500 to-${color}-600 text-white rounded-lg hover:from-${color}-600 hover:to-${color}-700 disabled:opacity-50`}
              >
                {loading ? (
                  <Loader className="w-5 h-5 animate-spin" />
                ) : (
                  <Play className="w-5 h-5" />
                )}
                실행하기
              </button>
            </div>

            {/* 결과 */}
            <div className="bg-gray-50 rounded-xl shadow-sm border p-6 overflow-y-auto">
              <h3 className="text-lg font-semibold mb-4">결과</h3>
              {loading ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <Loader className="w-12 h-12 animate-spin text-blue-600 mb-4" />
                  <p className="text-gray-600">
                    에이전트가 작업을 처리하고 있습니다...
                  </p>
                </div>
              ) : result ? (
                <div className="space-y-4">
                  <div className="prose prose-sm max-w-none bg-white rounded-lg p-4 shadow-sm">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {result}
                    </ReactMarkdown>
                  </div>
                  {images.map((img, i) => (
                    <img
                      key={i}
                      src={toDataUri(img)}
                      alt=""
                      className="w-full rounded-lg shadow-md"
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                  <Sparkles className="w-12 h-12 mb-4" />
                  <p>실행 결과가 여기에 표시됩니다</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
